import { createAdminV2Error } from '@/admin-v2/foundation/errors';
import { AI_IMAGE_USER_ERROR_CODES } from '@/ai/image/errors';
import { calculateUpscalerOutputSize as calculateLegacyUpscalerOutputSize } from '@/ai/image/tool-contracts/upscaler/legacy-output-size';
import {
  UPSCALER_REALESRGAN_MAX_INPUT_PIXELS,
  calculateUpscalerOutputSize,
  estimateUpscalerGptDimensions,
  getUpscalerGptResolution,
  getUpscalerRealEsrganMaxFactor,
  getUpscalerRealEsrganScale,
  getUpscalerSourceDimensions,
} from '@/ai/image/tool-contracts/upscaler/output-size';
import {
  type TargetTier,
  assertSeedVrSourceSize,
  isSourceTooSmallForTier,
  resolveFactorForProvider,
  resolveSeedVrModeInput,
} from '@/ai/image/tool-contracts/upscaler/policy';
import {
  type AdminV2MediaMetaContext,
  type AdminV2MediaMetaSource,
  readAdminV2MediaMetaValue,
} from '../media-meta-context';
import type {
  AdminV2ComputedResolverDefinition,
  AdminV2MediaMetaTransformerDefinition,
  AdminV2PreflightValidatorDefinition,
} from '../tool-runtime-registries';

export const ADMIN_V2_UPSCALER_EDIT_PROMPT = `Create a faithful high-detail restoration of the input image.

Carefully improve sharpness, clarity, and detail across the entire image, including foreground, midground, and background.

Preserve all original visible content exactly: elements, shapes, layout, proportions, colors, lighting, patterns, surface markings, text, logos, symbols, overlays, imperfections, and small details.

Restore detail only by clarifying the existing structure. Follow the original edges, textures, colors, perspective, and scene geometry. Do not invent, remove, replace, merge, redraw, simplify, or reinterpret anything.

Keep the original exposure, white balance, color temperature, contrast, saturation, shadows, highlights, atmosphere, and mood unchanged. Avoid over-sharpening, over-smoothing, over-contrast, over-saturation, and artificial texture.

Do not beautify, retouch, stylize, relight, recolor, smooth, reshape, add bokeh, add cinematic effects, or make the image look like a different polished photo.

If faithful recovery is uncertain, keep the area slightly soft rather than guessing.`;

const UPSCALER_FLUX_STAGE_OUTPUT_PIXELS = 1_000_000;
const UPSCALER_IMAGE_FIELD_ID = 'image';
const UPSCALER_TARGET_TIERS = new Set<TargetTier>([
  '1k',
  '2k',
  '4k',
  '8k',
  '16k',
]);

type ImageMeta = {
  width: number;
  height: number;
  hasTransparentPixels: boolean;
  format?: string;
  sourceUrl?: string;
  [key: string]: unknown;
};

function isTargetTier(value: unknown): value is TargetTier {
  return (
    typeof value === 'string' && UPSCALER_TARGET_TIERS.has(value as TargetTier)
  );
}

function resolveUpscalerTargetTier(
  normalizedInput: Record<string, unknown>
): TargetTier {
  const value =
    normalizedInput.targetTier ??
    normalizedInput.target_tier ??
    normalizedInput.scale;
  if (isTargetTier(value)) {
    return value;
  }
  throw createAdminV2Error({
    code: 'ADMIN_V2_TOOL_UPSCALER_TARGET_TIER_INVALID',
    message: 'Upscaler target tier must be one of 1k, 2k, 4k, 8k, or 16k.',
    fieldPath: 'input.targetTier',
    status: 422,
  });
}

function resolveUpscalerInputImages(normalizedInput: Record<string, unknown>) {
  const value =
    normalizedInput[UPSCALER_IMAGE_FIELD_ID] ??
    normalizedInput.imageUrl ??
    normalizedInput.inputImageUrl;
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value];
  }
  if (
    Array.isArray(value) &&
    value.every((item) => typeof item === 'string' && item.trim().length > 0)
  ) {
    return value;
  }
  throw createAdminV2Error({
    code: 'ADMIN_V2_TOOL_UPSCALER_IMAGE_MISSING',
    message: 'Upscaler requires a source image.',
    fieldPath: `input.${UPSCALER_IMAGE_FIELD_ID}`,
    status: 422,
  });
}

function readFirstString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = readFirstString(item);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

function resolveUpscalerSourceImage(params: {
  normalizedInput: Record<string, unknown>;
  previousOutputs: Record<string, unknown>;
}) {
  for (const output of Object.values(params.previousOutputs)) {
    if (!output || typeof output !== 'object' || Array.isArray(output)) {
      continue;
    }
    const record = output as Record<string, unknown>;
    const firstOutputImage =
      readFirstString(record.image_urls) ?? readFirstString(record.image_url);
    if (firstOutputImage) {
      return firstOutputImage;
    }
  }
  return resolveUpscalerInputImages(params.normalizedInput)[0];
}

function isImageMeta(value: unknown): value is ImageMeta {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.width === 'number' &&
    Number.isFinite(record.width) &&
    record.width > 0 &&
    typeof record.height === 'number' &&
    Number.isFinite(record.height) &&
    record.height > 0 &&
    typeof record.hasTransparentPixels === 'boolean'
  );
}

function resolveUpscalerImageMetaFromSources(params: {
  mediaMetaContext?: AdminV2MediaMetaContext;
  candidateSources: AdminV2MediaMetaSource[];
}): ImageMeta {
  for (const metaSource of params.candidateSources) {
    const value = readAdminV2MediaMetaValue({
      context: params.mediaMetaContext,
      fieldId: UPSCALER_IMAGE_FIELD_ID,
      metaSource,
      metaPath: ['*'],
    });
    if (isImageMeta(value)) {
      return value;
    }
  }

  throw createAdminV2Error({
    code: 'ADMIN_V2_TOOL_UPSCALER_MEDIA_META_MISSING',
    message:
      'Upscaler requires image width, height, and transparency metadata.',
    fieldPath: `mediaMeta.${UPSCALER_IMAGE_FIELD_ID}`,
    status: 422,
  });
}

function resolveUpscalerImageMeta(
  mediaMetaContext?: AdminV2MediaMetaContext
): ImageMeta {
  return resolveUpscalerImageMetaFromSources({
    mediaMetaContext,
    candidateSources: ['providerReturnedMeta', 'clientProvidedMeta'],
  });
}

function resolveUpscalerOriginalImageMeta(
  mediaMetaContext?: AdminV2MediaMetaContext
): ImageMeta {
  return resolveUpscalerImageMetaFromSources({
    mediaMetaContext,
    candidateSources: ['clientProvidedMeta'],
  });
}

/** Transparent images bypass image editing, so the server owns these locks. */
export function normalizeUpscalerTransparentInput(params: {
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
}) {
  const original = readAdminV2MediaMetaValue({
    context: params.mediaMetaContext,
    fieldId: UPSCALER_IMAGE_FIELD_ID,
    metaSource: 'clientProvidedMeta',
    metaPath: ['*'],
  });
  // The request boundary rejects missing required image metadata. This keeps
  // generic runtime checks independent from an image-specific requirement.
  if (!isImageMeta(original)) return params.normalizedInput;
  if (!original.hasTransparentPixels) return params.normalizedInput;
  return {
    ...params.normalizedInput,
    scene: 'general',
    highQuality: false,
    detailLevel: false,
  };
}

function deriveOneMegapixelMeta(params: {
  originalMeta: ImageMeta;
  sourceUrl: string;
}): ImageMeta {
  const scale = Math.sqrt(
    UPSCALER_FLUX_STAGE_OUTPUT_PIXELS /
      (params.originalMeta.width * params.originalMeta.height)
  );
  return {
    ...params.originalMeta,
    width: Math.max(1, Math.round(params.originalMeta.width * scale)),
    height: Math.max(1, Math.round(params.originalMeta.height * scale)),
    sourceUrl: params.sourceUrl,
  };
}

function resolveUpscalerGptImageProviderInput(
  normalizedInput: Record<string, unknown>
) {
  return {
    prompt: ADMIN_V2_UPSCALER_EDIT_PROMPT,
    image_urls: resolveUpscalerInputImages(normalizedInput),
    size: 'auto',
    resolution: '1k',
    quality: 'low',
    n: 1,
  };
}

function resolveUpscalerFluxProviderInput(params: {
  normalizedInput: Record<string, unknown>;
  disableSafetyChecker: boolean;
}) {
  return {
    prompt: ADMIN_V2_UPSCALER_EDIT_PROMPT,
    images: resolveUpscalerInputImages(params.normalizedInput),
    aspect_ratio: 'match_input_image',
    output_megapixels: '1',
    output_format: 'png',
    output_quality: 100,
    disable_safety_checker: params.disableSafetyChecker,
  };
}

function resolveScenarioSize(
  input: Record<string, unknown>,
  imageMeta: ImageMeta
) {
  const size = Object.hasOwn(input, 'outputLongEdge')
    ? calculateUpscalerOutputSize(input, imageMeta)
    : calculateLegacyUpscalerOutputSize(input, imageMeta);
  if (size.exceedsLimit) {
    throw createAdminV2Error({
      code: AI_IMAGE_USER_ERROR_CODES.UPSCALE_SOURCE_TOO_SMALL,
      message:
        'Choose a smaller output size: the requested enlargement exceeds the model limit.',
      fieldPath: 'input.targetTier',
      status: 422,
    });
  }
  return size;
}

export const upscalerComputedResolverDefinitions = {
  upscalerScenarioGptInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext }) => {
      // Check the authorized GPT size estimate before paying for an edit.
      resolveScenarioSize(normalizedInput, {
        ...resolveUpscalerOriginalImageMeta(mediaMetaContext),
        ...estimateUpscalerGptDimensions(
          resolveUpscalerOriginalImageMeta(mediaMetaContext)
        ),
      });
      return {
        ...resolveUpscalerGptImageProviderInput(normalizedInput),
        prompt: buildUpscalerScenarioPrompt(normalizedInput),
        official_fallback: true,
      };
    },
  },
  upscalerHighQualityGptInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext }) => {
      const original = resolveUpscalerOriginalImageMeta(mediaMetaContext);
      resolveScenarioSize(normalizedInput, {
        ...original,
        ...getUpscalerSourceDimensions(normalizedInput, original),
      });
      return {
        ...resolveUpscalerGptImageProviderInput(normalizedInput),
        prompt: buildUpscalerScenarioPrompt(normalizedInput),
        resolution: getUpscalerGptResolution(normalizedInput),
        official_fallback: true,
      };
    },
  },
  upscalerScenarioSeedVrInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext, previousOutputs }) => {
      const imageMeta = resolveUpscalerImageMeta(mediaMetaContext);
      const size = resolveScenarioSize(normalizedInput, imageMeta);
      const editedImage =
        normalizedInput.highQuality === true
          ? (readFirstString(
              (
                previousOutputs.gptImageHighQuality as
                  | Record<string, unknown>
                  | undefined
              )?.image_urls
            ) ??
            readFirstString(
              (
                previousOutputs.gptImageHighQuality as
                  | Record<string, unknown>
                  | undefined
              )?.image_url
            ))
          : readFirstString(
              (
                previousOutputs.gptImageExtendedRestore as
                  | Record<string, unknown>
                  | undefined
              )?.image_urls
            );
      if (
        (normalizedInput.highQuality === true ||
          normalizedInput.scene !== 'general') &&
        !editedImage
      ) {
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_UPSCALER_EDIT_OUTPUT_MISSING',
          message: 'The image editing step returned no image.',
          fieldPath: 'outputs.gptImageExtendedRestore.image_urls',
          status: 502,
        });
      }
      const modeInput =
        normalizedInput.outputLongEdge || normalizedInput.outputShortEdge
          ? { upscale_mode: 'factor', upscale_factor: size.factor }
          : resolveSeedVrModeInput({ ...imageMeta, targetTier: size.tier });
      assertSeedVrSourceSize({ ...imageMeta, input: modeInput });
      return {
        image_url:
          normalizedInput.highQuality !== true &&
          normalizedInput.scene === 'general'
            ? resolveUpscalerInputImages(normalizedInput)[0]
            : editedImage,
        ...modeInput,
        noise_scale:
          normalizedInput.scene === 'general' || normalizedInput.scene === 'art'
            ? typeof normalizedInput.detailLevel === 'boolean'
              ? normalizedInput.detailLevel
                ? 0.4
                : 0
              : normalizedInput.detailLevel
            : 0,
        sync_mode: false,
      };
    },
  },
  upscalerScenarioPImageInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext, previousOutputs }) => {
      const size = resolveScenarioSize(
        normalizedInput,
        resolveUpscalerImageMeta(mediaMetaContext)
      );
      const editedImage =
        normalizedInput.highQuality === true
          ? (readFirstString(
              (
                previousOutputs.gptImageHighQuality as
                  | Record<string, unknown>
                  | undefined
              )?.image_urls
            ) ??
            readFirstString(
              (
                previousOutputs.gptImageHighQuality as
                  | Record<string, unknown>
                  | undefined
              )?.image_url
            ))
          : readFirstString(
              (
                previousOutputs.gptImageExtendedRestore as
                  | Record<string, unknown>
                  | undefined
              )?.image_urls
            );
      if (
        (normalizedInput.highQuality === true ||
          normalizedInput.scene !== 'general') &&
        !editedImage
      ) {
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_UPSCALER_EDIT_OUTPUT_MISSING',
          message: 'The image editing step returned no image.',
          fieldPath: 'outputs.gptImageExtendedRestore.image_urls',
          status: 502,
        });
      }
      return {
        image:
          normalizedInput.highQuality !== true &&
          normalizedInput.scene === 'general'
            ? resolveUpscalerInputImages(normalizedInput)[0]
            : editedImage,
        ...(normalizedInput.outputLongEdge || normalizedInput.outputShortEdge
          ? { upscale_mode: 'factor', factor: size.factor }
          : { upscale_mode: 'target', target: size.targetMegapixels }),
        enhance_details: false,
        enhance_realism: false,
        disable_safety_checker: true,
      };
    },
  },
  upscalerGptImageOfficialProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) => ({
      ...resolveUpscalerGptImageProviderInput(normalizedInput),
      moderation: 'low',
      output_format: 'png',
    }),
  },
  upscalerGptImageExtendedProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) => ({
      ...resolveUpscalerGptImageProviderInput(normalizedInput),
      official_fallback: true,
    }),
  },
  upscalerEditProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) =>
      resolveUpscalerFluxProviderInput({
        normalizedInput,
        disableSafetyChecker: true,
      }),
  },
  upscalerBoostFluxProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput }) =>
      resolveUpscalerFluxProviderInput({
        normalizedInput,
        disableSafetyChecker: true,
      }),
  },
  upscalerSeedVrProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext, previousOutputs }) => ({
      image_url: resolveUpscalerSourceImage({
        normalizedInput,
        previousOutputs,
      }),
      ...resolveSeedVrModeInput({
        ...resolveUpscalerImageMeta(mediaMetaContext),
        targetTier: resolveUpscalerTargetTier(normalizedInput),
      }),
      output_format: 'png',
    }),
  },
  upscalerPImageProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, previousOutputs }) => {
      const targetTier = resolveUpscalerTargetTier(normalizedInput);
      if (targetTier !== '8k' && targetTier !== '16k') {
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_UPSCALER_P_IMAGE_TIER_INVALID',
          message: 'P-Image upscale only supports 8k and 16k tiers.',
          fieldPath: 'input.targetTier',
          status: 422,
        });
      }
      return {
        image: resolveUpscalerSourceImage({ normalizedInput, previousOutputs }),
        upscale_mode: 'target',
        target: targetTier === '16k' ? 128 : 33,
        output_format: 'png',
        enhance_details: false,
        enhance_realism: false,
        disable_safety_checker: true,
      };
    },
  },
  upscalerRealEsrganProviderInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext }) => {
      const imageMeta = resolveUpscalerImageMeta(mediaMetaContext);
      return {
        image: resolveUpscalerInputImages(normalizedInput)[0],
        scale: resolveFactorForProvider({
          width: imageMeta.width,
          height: imageMeta.height,
          targetTier: resolveUpscalerTargetTier(normalizedInput),
        }),
      };
    },
  },
  upscalerScenarioRealEsrganInput: {
    outputType: 'json',
    resolver: ({ normalizedInput, mediaMetaContext }) => {
      const imageMeta = resolveUpscalerImageMeta(mediaMetaContext);
      return {
        image: resolveUpscalerInputImages(normalizedInput)[0],
        scale: getUpscalerRealEsrganScale(normalizedInput, imageMeta),
      };
    },
  },
} satisfies Record<string, AdminV2ComputedResolverDefinition>;

export function buildUpscalerScenarioPrompt(input: Record<string, unknown>) {
  // Stack shared boundaries, one mode, and enabled adjustments into one edit.
  // Adjustments may change their named properties, never identity or content.
  const instructions = [
    'Task:\nFaithfully enhance the supplied image for upscaling. Edit the existing image while keeping its framing, aspect ratio, composition, and visual style.',
    'Preserve:\nKeep subject identity, object geometry, materials, brand-mark designs, written content, and layout. Do not add, remove, or replace objects, symbols, or lettering. Keep the original colors, exposure, lighting, and background except for the specific properties named in a selected adjustment below. Those adjustments never authorize changes to identity, geometry, branding, or written content.',
    'Recovery:\nClarify edges and textures supported by visible evidence. Where detail is genuinely missing or unreadable, preserve the ambiguity instead of inventing plausible detail. Avoid sharpening halos, invented texture, and excessive smoothing. Apply the mode treatment and only the selected adjustments below.',
  ];
  switch (input.scene) {
    case 'general':
      if (input.highQuality !== true)
        throw createAdminV2Error({
          code: 'ADMIN_V2_TOOL_UPSCALER_SCENE_INVALID',
          message: 'This scene does not use image editing.',
          status: 422,
        });
      instructions.push(
        'Mode: General\nImprove existing detail and clarity faithfully, preserving the original subjects, composition, colors and lighting.'
      );
      break;
    case 'portrait':
      instructions.push(
        'Mode: Portrait\nGently clarify existing hair, clothing, and portrait detail. Preserve the same person, age, expression, gaze, facial proportions, skin tone, and natural skin texture. Keep facial features as shown unless face repair is selected below. Do not beautify, reshape the face, smooth skin, or introduce studio lighting or background blur.'
      );
      if (input.faceRepair === true) {
        instructions.push(
          'Selected adjustment: Face repair\nReduce facial blur and restore the continuity of visible facial edges using the source as the sole reference. Make recognizable features clearer without changing their shape or inventing eyelashes, teeth, pores, or other missing detail. Preserve identity and expression; leave uncertain facial areas soft.'
        );
      }
      break;
    case 'product':
      instructions.push(
        'Mode: Product\nClarify existing product edges and material texture for a faithful product photograph. Preserve the exact design, proportions, parts, surface finish, packaging, logos, and label content. Do not redesign, retouch away product features, replace the background, or add promotional styling. Change shadows, white balance, or brightness only when the corresponding adjustment is selected below.'
      );
      if (input.removeShadows === true) {
        // Retain the meaning of in-flight jobs saved with the previous Tool version.
        instructions.push(
          "Selected adjustment: Remove background shadows\nRemove cast shadows from the background while retaining the product's own shading, reflections, contours, and three-dimensional shape. Continue the existing background naturally in the affected areas; do not replace the background or flatten the product."
        );
      }
      if (input.addNaturalShadow === true) {
        instructions.push(
          "Selected adjustment: Add natural shadow\nAdd a subtle, soft contact shadow directly beneath the product so it sits naturally on the existing surface. Match the existing light direction and perspective. If a suitable natural contact shadow already exists, preserve it instead of adding a second shadow. Keep the shadow close to the base; avoid long, hard, dark, floating, or theatrical shadows. Preserve the product's own shading, reflections, shape, colors, material, logos, and text. Do not replace the background, add a new surface or objects, or relight the product."
        );
      }
      if (input.correctColors === true) {
        instructions.push(
          'Selected adjustment: Color correction\nCorrect an overall color cast with a restrained white-balance adjustment. Keep the product recognizable in the same color variant; do not selectively recolor parts, change printed color relationships, or boost saturation for decoration.'
        );
      }
      if (input.brighten === true) {
        instructions.push(
          'Selected adjustment: Brighten\nGently lift underexposed tones to make the product easier to see. Preserve highlight detail, material texture, and color relationships. Avoid clipped highlights, washed-out surfaces, or a new lighting direction.'
        );
      }
      break;
    case 'text':
      instructions.push(
        'Mode: Text and graphics\nGently clean compression artifacts around existing graphic shapes. Preserve wording, case, numbers, punctuation, glyph structure, font style, spacing, line breaks, alignment, and graphic layout. Keep flat colors and intentional line thickness. Keep letter edges as shown unless text deblur is selected below. Do not translate, correct spelling, typeset replacement text, or infer unreadable characters.'
      );
      if (input.textDeblur === true) {
        instructions.push(
          'Selected adjustment: Text deblur\nReduce blur at the edges of visible, recognizable characters and graphic strokes. Improve separation of existing strokes without changing glyph structure, stroke weight, spacing, or wording. Use only the visible source; ambiguous or unreadable characters must remain ambiguous rather than becoming guessed text.'
        );
      }
      break;
    case 'art':
      instructions.push(
        'Mode: Illustration and AI art\nClean compression artifacts and clarify existing forms while preserving the original medium, linework, brush strokes, palette, shading, and intentional texture. Keep stylized anatomy, shapes, and soft edges intentional. Do not convert the image to photography, restyle it, fill empty areas, or add decorative detail.'
      );
      break;
    default:
      throw createAdminV2Error({
        code: 'ADMIN_V2_TOOL_UPSCALER_SCENE_INVALID',
        message: 'This scene does not use image editing.',
        fieldPath: 'input.scene',
        status: 422,
      });
  }
  return instructions.join('\n\n');
}

export const upscalerPreflightValidatorDefinitions = {
  upscalerSourceSize: {
    validator: ({ normalizedInput, mediaMetaContext, errorCode }) => {
      const targetTier = resolveUpscalerTargetTier(normalizedInput);
      const imageMeta = resolveUpscalerImageMeta(mediaMetaContext);
      if (
        isSourceTooSmallForTier({
          width: imageMeta.width,
          height: imageMeta.height,
          targetTier,
        })
      ) {
        throw createAdminV2Error({
          code: errorCode ?? AI_IMAGE_USER_ERROR_CODES.UPSCALE_SOURCE_TOO_SMALL,
          message: 'Input image is too small for selected target resolution.',
          fieldPath: `input.${UPSCALER_IMAGE_FIELD_ID}`,
          status: 422,
        });
      }
    },
  },
  upscalerRealEsrganTransparentInput: {
    validator: ({ normalizedInput, mediaMetaContext }) => {
      const targetTier = resolveUpscalerTargetTier(normalizedInput);
      const imageMeta = resolveUpscalerImageMeta(mediaMetaContext);
      if (!imageMeta.hasTransparentPixels) {
        return;
      }
      if (
        isSourceTooSmallForTier({
          width: imageMeta.width,
          height: imageMeta.height,
          targetTier,
        })
      ) {
        throw createAdminV2Error({
          code: AI_IMAGE_USER_ERROR_CODES.UPSCALE_SOURCE_TOO_SMALL,
          message: 'Input image is too small for selected target resolution.',
          fieldPath: `input.${UPSCALER_IMAGE_FIELD_ID}`,
          status: 422,
        });
      }
      if (
        imageMeta.width * imageMeta.height >
        UPSCALER_REALESRGAN_MAX_INPUT_PIXELS
      ) {
        throw createAdminV2Error({
          code: AI_IMAGE_USER_ERROR_CODES.UPSCALE_TRANSPARENT_IMAGE_TOO_LARGE,
          message: 'Transparent image is too large for Real-ESRGAN upscale.',
          fieldPath: `input.${UPSCALER_IMAGE_FIELD_ID}`,
          status: 422,
        });
      }
    },
  },
  upscalerScenarioRealEsrganTransparentInput: {
    validator: ({ normalizedInput, mediaMetaContext }) => {
      const imageMeta = resolveUpscalerImageMeta(mediaMetaContext);
      if (!imageMeta.hasTransparentPixels) {
        return;
      }
      if (
        imageMeta.width * imageMeta.height >
        UPSCALER_REALESRGAN_MAX_INPUT_PIXELS
      ) {
        throw createAdminV2Error({
          code: AI_IMAGE_USER_ERROR_CODES.UPSCALE_TRANSPARENT_IMAGE_TOO_LARGE,
          message: 'Transparent image is too large for Real-ESRGAN upscale.',
          fieldPath: `input.${UPSCALER_IMAGE_FIELD_ID}`,
          status: 422,
        });
      }
      if (
        getUpscalerRealEsrganScale(normalizedInput, imageMeta) >
        getUpscalerRealEsrganMaxFactor(imageMeta)
      ) {
        throw createAdminV2Error({
          code: AI_IMAGE_USER_ERROR_CODES.UPSCALE_SOURCE_TOO_SMALL,
          message: 'Input image is too small for selected target resolution.',
          fieldPath: `input.${UPSCALER_IMAGE_FIELD_ID}`,
          status: 422,
        });
      }
    },
  },
} satisfies Record<string, AdminV2PreflightValidatorDefinition>;

const upscalerRestoreOneMegapixelTransformer: AdminV2MediaMetaTransformerDefinition =
  {
    transformer: ({ mediaMetaContext, outputSlots, targetFieldId }) => {
      const outputUrl =
        readFirstString(outputSlots.image_urls) ??
        readFirstString(outputSlots.image_url);
      if (!outputUrl) {
        return undefined;
      }
      const originalMeta = resolveUpscalerOriginalImageMeta(mediaMetaContext);
      return {
        providerReturnedMeta: {
          [targetFieldId ?? UPSCALER_IMAGE_FIELD_ID]: deriveOneMegapixelMeta({
            originalMeta,
            sourceUrl: outputUrl,
          }),
        },
      };
    },
  };

export const upscalerMediaMetaTransformerDefinitions = {
  upscalerGptEstimatedOutput: {
    transformer: ({ mediaMetaContext, outputSlots, targetFieldId }) => {
      const outputUrl = readFirstString(outputSlots.image_urls);
      if (!outputUrl) return undefined;
      const original = resolveUpscalerOriginalImageMeta(mediaMetaContext);
      return {
        providerReturnedMeta: {
          [targetFieldId ?? UPSCALER_IMAGE_FIELD_ID]: {
            ...original,
            ...estimateUpscalerGptDimensions(original),
            sourceUrl: outputUrl,
          },
        },
      };
    },
  },
  upscalerHighQualityEstimatedOutput: {
    transformer: ({
      normalizedInput,
      mediaMetaContext,
      outputSlots,
      targetFieldId,
    }) => {
      const outputUrl =
        readFirstString(outputSlots.image_urls) ??
        readFirstString(outputSlots.image_url);
      if (!outputUrl) return undefined;
      const original = resolveUpscalerOriginalImageMeta(mediaMetaContext);
      return {
        providerReturnedMeta: {
          [targetFieldId ?? UPSCALER_IMAGE_FIELD_ID]: {
            ...original,
            ...estimateUpscalerGptDimensions(
              original,
              getUpscalerGptResolution(normalizedInput)
            ),
            sourceUrl: outputUrl,
          },
        },
      };
    },
  },
  upscalerRestoreOneMegapixelOutput: upscalerRestoreOneMegapixelTransformer,
} satisfies Record<string, AdminV2MediaMetaTransformerDefinition>;
